import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Cadeau offert : garde dans le panier autant de cadeaux que de produits
 * concernés (ajout, retrait, changement de quantité). La gratuité vient de la
 * réduction automatique « Achetez X, obtenez Y » ; ce module ne gère que la ligne.
 * Configuration rendue par snippets/scripts.liquid (#bol-cadeau-config).
 */

const GIFT_PROPERTY = '_cadeau';
const SOURCE = 'bol-cadeau';

const configElement = document.getElementById('bol-cadeau-config');
const config = configElement ? JSON.parse(configElement.textContent || '{}') : null;

if (config?.giftVariantId && Array.isArray(config.productIds) && config.productIds.length) {
  const giftVariantId = Number(config.giftVariantId);
  const qualifyingIds = new Set(config.productIds.map(Number));
  let running = false;
  let again = false;

  /** @returns {string[]} */
  const cartSectionIds = () =>
    [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter((id) => Boolean(id));

  const fetchCart = async () => {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  };

  const syncOnce = async () => {
    const cart = await fetchCart();
    let wanted = 0;
    for (const item of cart.items) {
      if (qualifyingIds.has(item.product_id)) wanted += item.quantity;
    }
    const giftLines = cart.items.filter(
      (item) => item.variant_id === giftVariantId && item.properties && item.properties[GIFT_PROPERTY]
    );
    const current = giftLines.reduce((sum, item) => sum + item.quantity, 0);
    if (current === wanted) return;

    const sections = cartSectionIds().join(',');
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: wanted > current ? 'update' : 'remove',
        context: 'cart',
        lines: [{ merchandiseId: String(giftVariantId), quantity: wanted }],
        promise: deferred.promise,
      })
    );

    try {
      let response;
      if (giftLines.length) {
        response = await fetch(
          Theme.routes.cart_change_url,
          fetchConfig('json', {
            body: JSON.stringify({ id: giftLines[0].key, quantity: wanted, sections, sections_url: window.location.pathname }),
          })
        );
      } else {
        response = await fetch(
          Theme.routes.cart_add_url,
          fetchConfig('json', {
            body: JSON.stringify({
              items: [{ id: giftVariantId, quantity: wanted, properties: { [GIFT_PROPERTY]: config.label || 'Offert' } }],
              sections,
              sections_url: window.location.pathname,
            }),
          })
        );
      }
      const data = await response.json();
      if (data.status || data.errors) throw new Error(data.description || data.message || 'Cadeau non ajouté');
      const updatedCart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
        detail: { sections: data.sections, items: updatedCart.items, itemCount: updatedCart.item_count, source: SOURCE, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      console.warn('[bol-cadeau]', error);
    }
  };

  const sync = async () => {
    if (running) {
      again = true;
      return;
    }
    running = true;
    try {
      do {
        again = false;
        await syncOnce();
      } while (again);
    } catch (error) {
      console.warn('[bol-cadeau]', error);
    } finally {
      running = false;
    }
  };

  /** @param {CartLinesUpdateEvent} event */
  const onCartUpdate = (event) => {
    const settle = event.promise ?? Promise.resolve(null);
    settle
      .then((result) => {
        if (result?.detail?.source === SOURCE) return;
        sync();
      })
      .catch(() => sync());
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);
  sync();
}
